import type { Run } from '../wiki/types';

/** A single word as it will appear in the level. */
export interface Tok {
  text: string;
  bold: boolean;
  italic: boolean;
  link?: string;
  red?: boolean;
  redirect?: boolean;
  /** Link occurrence id; consecutive words of one multi-word link share it. */
  occ?: number;
  /** Inside (parentheses): becomes a one-way platform. */
  paren: boolean;
  /** Mostly digits (years, quantities): becomes a timed/blinking platform. */
  number: boolean;
  /** Last word of a sentence: a hole follows it. */
  sentenceEnd: boolean;
  /** No whitespace between this word and the next (e.g. link + suffix). */
  joinNext: boolean;
  cites: string[];
  cn: boolean;
}

const ABBREV = new Set(
  (
    'e.g. i.e. etc. vs. v. cf. c. ca. mr. mrs. ms. dr. st. jr. sr. inc. ltd. co. no. nos. approx. al. fl. b. d. r. ' +
    'mt. ft. gen. col. lt. sgt. capt. prof. rev. vol. vols. pp. p. ed. eds. jan. feb. mar. apr. jun. jul. aug. sep. ' +
    'sept. oct. nov. dec. est. dept. univ. assn. bros. fig. figs. op. ch. sec. corp. gov. pres. sen. rep. adm. cmdr. ' +
    'hon. messrs. mme. mlle. sp. spp. var. subsp. syn. ibid. viz. mgr. rt. ave. blvd. rd. hwy. mi. oz. lb. lbs. in.'
  ).split(' '),
);

const TRAIL = /["'”’)\]]+$/;

function isAbbrev(word: string): boolean {
  const w = word.replace(TRAIL, '');
  if (ABBREV.has(w.toLowerCase())) return true;
  if (/^\p{Lu}\.$/u.test(w)) return true; // initials: "J."
  if (/^(\p{L}\.){2,}$/u.test(w)) return true; // "U.S."
  return false;
}

function isNumberWord(text: string): boolean {
  const core = text.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
  if (!core || !/\p{N}/u.test(core)) return false;
  const digits = (core.match(/\p{N}/gu) ?? []).length;
  const letters = (core.match(/\p{L}/gu) ?? []).length;
  return digits >= letters * 1.5;
}

export function tokenize(runs: Run[], occCounter: { n: number }): Tok[] {
  const toks: Tok[] = [];
  let space = true;
  for (const r of runs) {
    if (r.cite) {
      toks[toks.length - 1]?.cites.push(r.text);
      continue;
    }
    if (r.cn) {
      const t = toks[toks.length - 1];
      if (t) t.cn = true;
      continue;
    }
    for (const piece of r.text.split(/(\s+)/)) {
      if (!piece) continue;
      if (/^\s+$/.test(piece)) {
        space = true;
        continue;
      }
      const prev = toks[toks.length - 1];
      if (prev && !space) prev.joinNext = true;
      let occ: number | undefined;
      if (r.link) occ = prev && prev.link === r.link && prev.occ !== undefined ? prev.occ : occCounter.n++;
      toks.push({
        text: piece,
        bold: !!r.bold,
        italic: !!r.italic,
        link: r.link,
        red: r.red,
        redirect: r.redirect,
        occ,
        paren: false,
        number: false,
        sentenceEnd: false,
        joinNext: false,
        cites: [],
        cn: false,
      });
      space = false;
    }
  }

  let depth = 0;
  for (let i = 0; i < toks.length; i++) {
    const t = toks[i];
    const opens = (t.text.match(/\(/g) ?? []).length;
    const closes = (t.text.match(/\)/g) ?? []).length;
    t.paren = depth > 0 || opens > 0;
    depth = Math.max(0, depth + opens - closes);
    t.number = isNumberWord(t.text);
    const next = toks[i + 1];
    t.sentenceEnd =
      !!next &&
      !t.joinNext &&
      /[.!?]["'”’)\]]*$/.test(t.text) &&
      !isAbbrev(t.text) &&
      /^["'“‘(\[]?[\p{Lu}\p{N}]/u.test(next.text);
  }
  return toks;
}
